import React, { useState, useRef, useEffect } from 'react';

const FileUploadStep = ({ question, value, onAnswer, onNext }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(value?.preview || null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // §watchdog: absolute guarantee the "Processing…" spinner can never get stuck.
  // The encode is already timeout-guarded, but if ANYTHING unforeseen stalls
  // (iframe quirk, huge file), force-clear after a hard cap with actionable text
  // instead of trapping the patient on an endless spinner.
  useEffect(() => {
    if (!uploading) return undefined;
    const t = setTimeout(() => {
      setUploading(false);
      setPreview((p) => {
        if (!p) setError('That file took too long to process. Please try a clear photo (JPG/PNG) of your ID, or a smaller file.');
        return p;
      });
    }, 15000);
    return () => clearTimeout(t);
  }, [uploading]);

  const handleFileSelect = (e) => processFile(e.target.files && e.target.files[0]);
  const handleDrop = (e) => {
    e.preventDefault();
    processFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]);
  };
  const openPicker = () => { if (fileInputRef.current) fileInputRef.current.click(); };

  const processFile = async (selectedFile) => {
    if (!selectedFile) return;

    // Accept any image (jpeg/png/webp/heic/heif from phones & screenshots) or a
    // PDF. Some browsers report an empty MIME type for HEIC, so fall back to the
    // file extension. (Previously JPEG-only, which silently rejected PNG/HEIC and
    // left "Continue" grayed out.)
    const type = selectedFile.type || '';
    const isImageOrPdf =
      type.startsWith('image/') ||
      type === 'application/pdf' ||
      /\.(jpe?g|png|webp|heic|heif|pdf)$/i.test(selectedFile.name || '');
    if (!isImageOrPdf) {
      setError('Please upload a photo (JPG, PNG, HEIC) or a PDF.');
      return;
    }

    // Validate file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError('');
    setUploading(true);
    setFile(selectedFile);

    // §id-upload-perf: a raw phone photo (5–10MB) becomes a ~1.33× base64 string
    // that lives in `answers` state, is carried through every later step, and gets
    // relayed post-submit — which janks the embedded (iframe) flow for seconds.
    // Downscale + re-encode images to a modest JPEG first (typically 15–25×
    // smaller) so state updates, the preview render, and the relay are all fast.
    // Non-images (PDF) and anything compression can't handle fall back to the raw
    // base64 path. CRITICAL: compression must NEVER hang the spinner — encodeFile
    // always settles (timeout-guarded) and `uploading` is reset in `finally`.
    try {
      const base64 = await encodeFile(selectedFile);
      setPreview(base64);
    } catch (err) {
      setError('Error processing file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Encode to a data URL, preferring a compressed JPEG for images. Guaranteed to
  // settle: if compression can't finish (undecodable format, iframe CSP blocking
  // the decode, or an unexpectedly slow pass) it resolves null within the timeout
  // and we fall back to the raw file, which always settles.
  const encodeFile = async (file) => {
    const type = file.type || '';
    const isPdf = type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
    const looksImage =
      type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(file.name || '');
    if (looksImage && !isPdf) {
      const compressed = await Promise.race([
        compressImage(file).catch(() => null),
        new Promise((resolve) => setTimeout(() => resolve(null), 4000)),
      ]);
      if (compressed) return compressed;
    }
    return convertToBase64(file);
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Downscale to a max long-edge and re-encode as JPEG. Decodes via
  // createImageBitmap (a real Promise — no <img>/blob-URL load events that can
  // silently never fire inside a sandboxed iframe, which is what hung the
  // spinner). Rejects for formats the browser can't decode (e.g. HEIC) so the
  // caller falls back to the raw file.
  const compressImage = async (file, maxDim = 1600, quality = 0.82) => {
    if (typeof createImageBitmap !== 'function') throw new Error('no createImageBitmap');
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    if (!width || !height) { bitmap.close && bitmap.close(); throw new Error('empty image'); }
    const longest = Math.max(width, height);
    if (longest > maxDim) {
      const scale = maxDim / longest;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (!dataUrl || dataUrl === 'data:,') throw new Error('encode failed');
      return dataUrl;
    } finally {
      bitmap.close && bitmap.close();
    }
  };

  const handleRemove = () => {
    setFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleContinue = () => {
    if (!preview) {
      setError('Please upload a file to continue');
      return;
    }

    // Derive the type from the (possibly re-encoded) preview so it matches the
    // bytes we actually relay — an image compressed to JPEG must report image/jpeg
    // even if the original was a PNG. Falls back to the file's type, then JPEG.
    const previewMime = (/^data:([^;]+);/.exec(preview || '') || [])[1] || file?.type || 'image/jpeg';
    onAnswer({
      fileName: file?.name || 'uploaded-file',
      fileType: previewMime,
      preview: preview,
      uploadedAt: new Date().toISOString()
    });
    onNext();
  };

  const isImage = file?.type?.startsWith('image/') || preview?.startsWith('data:image');

  return (
    <div data-testid={`step-${question.id}`}>
      {/* Question */}
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">
        {question.question}
      </h2>

      {/* Subtitle */}
      {question.subtitle && (
        <p className="text-center text-gray-500 mb-6">
          {question.subtitle}
        </p>
      )}

      {/* Upload Area */}
      <div className="mb-6">
        {!preview ? (
          <div
            role="button"
            tabIndex={0}
            data-testid="file-drop"
            onClick={openPicker}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); } }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {uploading ? (
                <>
                  <svg className="animate-spin w-10 h-10 text-[#2d8a5f] mb-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <p className="text-sm text-gray-500">Processing...</p>
                </>
              ) : (
                <>
                  <svg className="w-10 h-10 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">JPG, PNG, HEIC or PDF (max 10MB)</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              onClick={(e) => e.stopPropagation()}
              data-testid="file-input"
            />
          </div>
        ) : (
          <div className="relative">
            {isImage ? (
              <img
                src={preview}
                alt="Preview"
                className="w-full h-48 object-contain bg-gray-100 rounded-xl"
              />
            ) : (
              <div className="w-full h-48 bg-gray-100 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-500">{file?.name || 'PDF Document'}</p>
                </div>
              </div>
            )}
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
              data-testid="remove-file"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-sm text-center mb-4" data-testid="upload-error">
          {error}
        </p>
      )}

      {/* Continue Button */}
      <button
        onClick={handleContinue}
        disabled={!preview || uploading}
        className="btn-primary"
        data-testid="continue-button"
      >
        Continue
      </button>

      {/* Skip option if file is optional */}
      {question.optional && !preview && (
        <button
          onClick={() => { onAnswer(null); onNext(); }}
          className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700"
        >
          Skip this step
        </button>
      )}
    </div>
  );
};

export default FileUploadStep;
